import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  ["ro", "🇷🇴 Română"], ["en", "🇬🇧 English"], ["fr", "🇫🇷 Français"], ["de", "🇩🇪 Deutsch"],
  ["it", "🇮🇹 Italiano"], ["es", "🇪🇸 Español"], ["pt", "🇵🇹 Português"],
  ["pt-BR", "🇧🇷 Português (Brasil)"], ["sr", "🇷🇸 Srpski"],
];

const Shell = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#0d1b2a", padding: "18px 12px 40px" }}>
    <div style={{ maxWidth: 760, margin: "0 auto", background: "#f7f3e8", borderRadius: 12, border: "1px solid #C8A96A", padding: "22px 18px" }}>
      {children}
    </div>
  </div>
);

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #C8A96A",
  background: "#fdfbf5", fontSize: 15, color: "#14204a", outline: "none", boxSizing: "border-box",
};
const labelStyle = { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "#7a5e2a", textTransform: "uppercase", display: "block", margin: "12px 0 4px" };

function Field({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label style={labelStyle}>{label}{required ? " *" : ""}</label>
      <input style={inputStyle} type={type} value={value} required={required}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function YesNo({ label, value, onChange, L }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: "flex", gap: 8 }}>
        {["yes", "no"].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, fontWeight: 700, cursor: "pointer",
              border: value === v ? "2px solid #0d1b2a" : "1px solid #C8A96A",
              background: value === v ? "#0d1b2a" : "#fdfbf5",
              color: value === v ? "#e8d59a" : "#14204a",
            }}>
            {v === "yes" ? L.yes : L.no}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PetitionPublicPage() {
  const { token } = useParams();
  const [lang, setLang] = useState(null);
  const [L, setL] = useState(null);
  const [lodgeName, setLodgeName] = useState("");
  const [ptype, setPtype] = useState("initiation");
  const [logoOk, setLogoOk] = useState(true);
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("load"); // load | form | sign | done
  const [busy, setBusy] = useState(false);
  const [cvName, setCvName] = useState("");
  const [err, setErr] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const [d, setD] = useState({
    full_name: "", age: "", birth_date: "", birth_city: "", birth_country: "",
    res_street: "", res_postal: "", res_city: "", res_country: "",
    bus_street: "", bus_postal: "", bus_city: "", bus_country: "",
    occupation: "", employer: "", resided_6m: "", resided_6m_where: "", resided_10y: "",
    convicted: "", convicted_details: "", prev_petition: "", prev_details: "",
    incompatible: "", incompatible_details: "", impediment: "", impediment_details: "",
    mother_lodge: "",
  });
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    // Initial load in the sender's app language (returned by the backend):
    // the language-chooser heading is shown ONLY in that language.
    axios.get(`${API}/public/petitions/${token}`)
      .then(({ data }) => {
        setL(data.labels);
        setLang(data.lang);
        setLodgeName(data.lodge_name);
        setStatus(data.status);
        setPtype(data.ptype || "initiation");
        setStep(data.status === "sent" ? "lang" : "done");
      })
      .catch((e) => setErr(e?.response?.status === 404 ? "Link invalid." : "Network error."));
    // eslint-disable-next-line
  }, [token]);

  const loadLang = async (lg) => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/public/petitions/${token}`, { params: { lang: lg } });
      setL(data.labels);
      setLang(lg);
      setStep(data.status === "sent" ? "form" : "done");
    } catch (e) {
      setErr(e?.response?.status === 404 ? "Link invalid." : "Network error.");
    } finally {
      setBusy(false);
    }
  };

  const uploadCv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/public/petitions/${token}/cv`, fd);
      setCvName(file.name);
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    }
  };

  // Signature pad
  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height };
  };
  const sigStart = (e) => {
    e.preventDefault(); drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const sigMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.strokeStyle = "#14204a";
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
    setHasInk(true);
  };
  const sigClear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/public/petitions/${token}`, { lang, data: d, signature });
      setStatus("submitted");
      setStep("done");
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (err) return <Shell><p style={{ color: "#8a1f1f", textAlign: "center", fontSize: 16 }}>{err}</p></Shell>;

  if (!L) return <Shell><p style={{ textAlign: "center" }}>...</p></Shell>;

  // STEP 1: language chooser — heading only in the sender's app language.
  if (step === "lang") {
    return (
      <Shell>
        {logoOk && (
          <div style={{ textAlign: "center", marginBottom: 10 }}>
            <img src={`${API}/public/petitions/${token}/logo`} alt="" data-testid="petition-lodge-logo"
              onError={() => setLogoOk(false)}
              style={{ width: 110, height: 110, objectFit: "contain", margin: "0 auto" }} />
          </div>
        )}
        <h1 style={{ fontFamily: "Georgia, serif", textAlign: "center", color: "#0d1b2a", letterSpacing: "0.1em", fontSize: 22, marginTop: 0 }}>
          {lodgeName || "..."}
        </h1>
        <p style={{ textAlign: "center", color: "#7a5e2a", fontWeight: 700, letterSpacing: "0.08em", fontSize: 13, textTransform: "uppercase" }} data-testid="petition-choose-language">
          {L.choose_language || "Choose your language"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 16 }} data-testid="petition-lang-grid">
          {LANGS.map(([c, n]) => (
            <button key={c} disabled={busy} onClick={() => loadLang(c)} data-testid={`petition-lang-${c}`}
              style={{
                padding: "16px 0", borderRadius: 10, border: "1.5px solid #C8A96A", background: "#fdfbf5",
                fontSize: 16, fontWeight: 700, color: "#0d1b2a", cursor: "pointer",
              }}>
              {n}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  // DONE screen
  if (step === "done") {
    return (
      <Shell>
        <div style={{ textAlign: "center", padding: "36px 8px" }} data-testid="petition-done">
          <div style={{ fontSize: 46 }}>✓</div>
          <h2 style={{ color: "#0d1b2a", fontFamily: "Georgia, serif" }}>{status === "sent" ? L.expired : L.success_title}</h2>
          <p style={{ color: "#555", lineHeight: 1.6 }}>{status !== "sent" ? (status === "submitted" || status === "generated" ? L.success_body : L.expired) : L.already_done}</p>
        </div>
      </Shell>
    );
  }

  // STEP 3: signature
  if (step === "sign") {
    return (
      <Shell>
        <h2 style={{ fontFamily: "Georgia, serif", color: "#0d1b2a", textAlign: "center" }}>{L.sign_title}</h2>
        <p style={{ color: "#555", fontSize: 14, textAlign: "center" }}>{L.sign_hint}</p>
        <canvas ref={canvasRef} width={700} height={240} data-testid="petition-signature-canvas"
          style={{ width: "100%", height: 190, background: "#fdfbf5", border: "1.5px dashed #9a7530", borderRadius: 8, touchAction: "none", display: "block" }}
          onPointerDown={sigStart} onPointerMove={sigMove}
          onPointerUp={() => { drawing.current = false; }} onPointerLeave={() => { drawing.current = false; }} />
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button onClick={() => setStep("form")} style={{ ...inputStyle, width: "auto", cursor: "pointer", fontWeight: 700 }}>{L.back}</button>
          <button onClick={sigClear} style={{ ...inputStyle, width: "auto", cursor: "pointer", fontWeight: 700 }}>{L.clear}</button>
          <button onClick={submit} disabled={!hasInk || busy} data-testid="petition-submit-btn"
            style={{
              flex: 1, borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 800, letterSpacing: "0.08em",
              background: hasInk ? "#0d1b2a" : "#9aa0ad", color: "#e8d59a", fontSize: 15,
            }}>
            {busy ? "..." : L.submit.toUpperCase()}
          </button>
        </div>
      </Shell>
    );
  }

  // STEP 2: form
  const _req = ["full_name","age","birth_date","birth_city","birth_country","res_street","res_postal","res_city","res_country","bus_street","bus_postal","bus_city","bus_country","occupation","employer",
    ...(ptype === "affiliation"
      ? ["mother_lodge","jurisdiction","init_date","passed_date","raised_date","degree","offices","active","leave_reason","motivation","suspended","convicted","incompatible"]
      : ["resided_6m","resided_6m_where","resided_10y","convicted","prev_petition","incompatible","impediment"])];
  const _cond = ptype === "affiliation"
    ? [["suspended","suspended_details"],["convicted","convicted_details"],["incompatible","incompatible_details"]]
    : [["convicted","convicted_details"],["prev_petition","prev_details"],["incompatible","incompatible_details"],["impediment","impediment_details"]];
  const canContinue = _req.every((k) => String(d[k] || "").trim())
    && _cond.every(([yn, det]) => d[yn] !== "yes" || String(d[det] || "").trim())
    && !!cvName;
  return (
    <Shell>
      {logoOk && (
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <img src={`${API}/public/petitions/${token}/logo`} alt="" data-testid="petition-lodge-logo"
            onError={() => setLogoOk(false)}
            style={{ width: 110, height: 110, objectFit: "contain", margin: "0 auto" }} />
        </div>
      )}
      <h1 style={{ fontFamily: "Georgia, serif", textAlign: "center", color: "#0d1b2a", fontSize: 21 }}>{ptype === "affiliation" ? L.title_aff : L.title}</h1>
      <p style={{ textAlign: "center", color: "#7a5e2a", fontSize: 13 }}>{lodgeName}</p>
      <p style={{ fontStyle: "italic", color: "#444", fontSize: 13.5, lineHeight: 1.6, borderLeft: "3px solid #C8A96A", paddingLeft: 10 }}>
        {(ptype === "affiliation" ? L.oath1_aff : L.oath1).replace("{name}", d.full_name || "…").replace("{lodge}", lodgeName)}
      </p>
      <p style={{ color: "#7a5e2a", fontSize: 12.5 }}>{L.form_hint}</p>

      <Field label={L.q_full_name} value={d.full_name} onChange={set("full_name")} required />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={L.q_age} value={d.age} onChange={set("age")} type="number" required />
        <Field label={L.q_birth_date} value={d.birth_date} onChange={set("birth_date")} type="date" required />
        <Field label={L.q_birth_city} value={d.birth_city} onChange={set("birth_city")} required />
        <Field label={L.q_birth_country} value={d.birth_country} onChange={set("birth_country")} required />
      </div>

      <h3 style={{ color: "#7a5e2a", marginTop: 18, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase" }}>{L.section_residence}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={L.q_res_street} value={d.res_street} onChange={set("res_street")} required />
        <Field label={L.q_res_postal} value={d.res_postal} onChange={set("res_postal")} required />
        <Field label={L.q_res_city} value={d.res_city} onChange={set("res_city")} required />
        <Field label={L.q_res_country} value={d.res_country} onChange={set("res_country")} required />
      </div>

      <h3 style={{ color: "#7a5e2a", marginTop: 18, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase" }}>{L.section_business}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={L.q_res_street} value={d.bus_street} onChange={set("bus_street")} required />
        <Field label={L.q_res_postal} value={d.bus_postal} onChange={set("bus_postal")} required />
        <Field label={L.q_res_city} value={d.bus_city} onChange={set("bus_city")} required />
        <Field label={L.q_res_country} value={d.bus_country} onChange={set("bus_country")} required />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label={L.q_occupation} value={d.occupation} onChange={set("occupation")} required />
        <Field label={L.q_employer} value={d.employer} onChange={set("employer")} required />
      </div>

      {ptype === "affiliation" ? (
        <>
          <h3 style={{ color: "#7a5e2a", marginTop: 18, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase" }}>{L.section_masonic}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label={L.q_mother_lodge} value={d.mother_lodge} onChange={set("mother_lodge")} required />
            <Field label={L.q_jurisdiction} value={d.jurisdiction} onChange={set("jurisdiction")} required />
            <Field label={L.q_init_date} value={d.init_date} onChange={set("init_date")} type="date" required />
            <Field label={L.q_passed_date} value={d.passed_date} onChange={set("passed_date")} type="date" required />
            <Field label={L.q_raised_date} value={d.raised_date} onChange={set("raised_date")} type="date" required />
            <Field label={L.q_degree} value={d.degree} onChange={set("degree")} required />
          </div>
          <Field label={L.q_offices} value={d.offices} onChange={set("offices")} required />
          <h3 style={{ color: "#7a5e2a", marginTop: 18, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase" }}>{L.section_status}</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <YesNo label={L.q_active} value={d.active} onChange={set("active")} L={L} />
            <Field label={L.q_leave_reason} value={d.leave_reason} onChange={set("leave_reason")} required />
            <Field label={L.q_motivation} value={d.motivation} onChange={set("motivation")} required />
            <YesNo label={L.q_suspended} value={d.suspended} onChange={set("suspended")} L={L} />
            {d.suspended === "yes" && <Field label={L.q_suspended_details} value={d.suspended_details} onChange={set("suspended_details")} required />}
            <YesNo label={L.q_convicted} value={d.convicted} onChange={set("convicted")} L={L} />
            {d.convicted === "yes" && <Field label={L.q_convicted_details} value={d.convicted_details} onChange={set("convicted_details")} required />}
            <YesNo label={L.q_incompatible} value={d.incompatible} onChange={set("incompatible")} L={L} />
            {d.incompatible === "yes" && <Field label={L.q_incompatible_details} value={d.incompatible_details} onChange={set("incompatible_details")} required />}
          </div>
        </>
      ) : (
      <div style={{ display: "grid", gap: 12, marginTop: 6 }}>
        <YesNo label={L.q_resided_6m} value={d.resided_6m} onChange={set("resided_6m")} L={L} />
        <Field label={L.q_resided_6m_where} value={d.resided_6m_where} onChange={set("resided_6m_where")} required />
        <Field label={L.q_resided_10y} value={d.resided_10y} onChange={set("resided_10y")} required />
        <YesNo label={L.q_convicted} value={d.convicted} onChange={set("convicted")} L={L} />
        {d.convicted === "yes" && <Field label={L.q_convicted_details} value={d.convicted_details} onChange={set("convicted_details")} required />}
        <YesNo label={L.q_prev_petition} value={d.prev_petition} onChange={set("prev_petition")} L={L} />
        {d.prev_petition === "yes" && <Field label={L.q_prev_details} value={d.prev_details} onChange={set("prev_details")} required />}
        <YesNo label={L.q_incompatible} value={d.incompatible} onChange={set("incompatible")} L={L} />
        {d.incompatible === "yes" && <Field label={L.q_incompatible_details} value={d.incompatible_details} onChange={set("incompatible_details")} required />}
        <YesNo label={L.q_impediment} value={d.impediment} onChange={set("impediment")} L={L} />
        {d.impediment === "yes" && <Field label={L.q_impediment_details} value={d.impediment_details} onChange={set("impediment_details")} required />}
      </div>
      )}

      <label style={labelStyle}>{ptype === "affiliation" ? L.doc_label : L.cv_label}</label>
      <label style={{ ...inputStyle, display: "block", cursor: "pointer", textAlign: "center", fontWeight: 700, color: cvName ? "#0d7a3c" : "#7a5e2a" }}>
        {cvName ? `✓ ${L.cv_uploaded}: ${cvName}` : "📎 PDF / JPG / PNG / DOCX"}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: "none" }}
          onChange={(e) => uploadCv(e.target.files?.[0])} data-testid="petition-cv-input" />
      </label>

      <p style={{ fontStyle: "italic", color: "#444", fontSize: 13, marginTop: 16 }}>{L.oath2}</p>
      <button onClick={() => setStep("sign")} disabled={!canContinue} data-testid="petition-continue-btn"
        style={{
          width: "100%", marginTop: 10, padding: "15px 0", borderRadius: 10, border: "none", cursor: "pointer",
          fontWeight: 800, letterSpacing: "0.1em", fontSize: 15,
          background: canContinue ? "#0d1b2a" : "#9aa0ad", color: "#e8d59a",
        }}>
        {L.continue.toUpperCase()} →
      </button>
    </Shell>
  );
}
